/**
 * The chrome. Precisely the things a host application would otherwise have provided.
 *
 * `InventoryModule` in the MUI skin already knows how to render itself with tabs, and the
 * shell deliberately does not use it: standalone, inventory is not one area inside a
 * larger product, it *is* the product, so its screens deserve real URLs and a real
 * navigation rather than tab state. Both mounting styles the skin documents are
 * supported precisely so this choice was available — the shell takes the second one,
 * iterating `INVENTORY_ROUTES` and registering each component against its own router.
 *
 * Everything below the app bar comes from `@inai/inventory-mui` unchanged. Nothing here
 * reimplements a screen, a form, a formatter or a client call.
 */

import {
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { INVENTORY_ROUTES } from '@inai/inventory-mui';
import { useEntitlements, useHealth, useOrganization } from '@inai/inventory-core/react';

import { useAuth } from '../auth/context';
import { SHELL_CONFIG } from '../config';
import { PlatformAppSwitcher } from './PlatformAppSwitcher';

const DRAWER_WIDTH = 232;

/**
 * Which organization this is.
 *
 * Requirement 3 of the slice. Embedded, the host's own chrome already tells a person which
 * company they are looking at; standalone nothing does, and "created in CRM but not
 * visible in Accounts" is almost always a tenant question. The value is the service's
 * answer from `GET /v1/organizations/me` — resolved from the verified claim server side —
 * and never the shell's own reading of the token, which is diagnostics and nothing more.
 */
function TenantBadge() {
  const { data: organization, isLoading } = useOrganization();
  const { data: entitlements } = useEntitlements();
  const { data: health } = useHealth();

  if (isLoading) return null;
  if (!organization) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label="no organization"
        sx={{ fontWeight: 600 }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Tooltip
        title={`Organization ${organization.id} · slug ${organization.slug} · status ${organization.status}`}
      >
        <Chip
          size="small"
          label={organization.name}
          sx={{ fontWeight: 700, maxWidth: 260 }}
          color={organization.status === 'active' ? 'default' : 'warning'}
        />
      </Tooltip>
      {entitlements?.plan ? (
        <Tooltip title="What the plan allows. The server re-checks every gated route.">
          <Chip size="small" variant="outlined" label={entitlements.plan} />
        </Tooltip>
      ) : null}
      {organization.storage_mode === 'remote' ? (
        <Tooltip title="This tenant's data lives in the canonical inventory service, not in this process.">
          <Chip size="small" variant="outlined" color="info" label="remote" />
        </Tooltip>
      ) : null}
      {health && health.status !== 'ok' ? (
        <Tooltip title={health.store.detail}>
          <Chip size="small" variant="outlined" color="warning" label="store degraded" />
        </Tooltip>
      ) : null}
    </Stack>
  );
}

function AccountMenu() {
  const { claims, signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const label =
    (typeof claims?.name === 'string' && claims.name) ||
    (typeof claims?.email === 'string' && claims.email) ||
    claims?.sub ||
    'Account';

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ maxWidth: 220 }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/diagnostics');
          }}
        >
          Diagnostics
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            signOut();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * The navigation.
 *
 * Filtered by `INVENTORY_ROUTES[].feature` against the tenant's entitlements — the same
 * rule `InventoryModule` applies to its tabs, and for the same reason: an essential-tier
 * user should not be shown a link that only ever 403s. Invariant 7 is not weakened by it.
 * The route still exists, typing the URL still reaches the screen, and the server refuses
 * on its own authority regardless. Hiding the link is a courtesy; the check is on the
 * server and nowhere else.
 */
function Navigation() {
  const { data: entitlements } = useEntitlements();
  const location = useLocation();

  const items = useMemo(
    () =>
      INVENTORY_ROUTES.filter((route) => route.primary).map((route) => ({
        ...route,
        allowed: !route.feature || !entitlements || entitlements.features.includes(route.feature),
      })),
    [entitlements],
  );

  return (
    <List dense sx={{ px: 1, py: 2 }}>
      {items
        .filter((item) => item.allowed)
        .map((item) => {
          const to = `/${item.path}`;
          const selected = location.pathname === to || location.pathname.startsWith(`${to}/`);
          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={to}
              selected={selected}
              sx={{ borderRadius: 1.5, mb: 0.25 }}
            >
              <ListItemText
                primary={item.label}
                secondary={item.description}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: 14,
                    fontWeight: selected ? 700 : 500,
                  },
                  '& .MuiListItemText-secondary': {
                    fontSize: 11,
                    display: { xs: 'none', md: 'block' },
                  },
                }}
              />
            </ListItemButton>
          );
        })}

      <Divider sx={{ my: 1.5 }} />

      <ListItemButton
        component={NavLink}
        to="/plan"
        selected={location.pathname === '/plan'}
        sx={{ borderRadius: 1.5 }}
      >
        <ListItemText primary="Plan" sx={{ '& .MuiListItemText-primary': { fontSize: 14 } }} />
      </ListItemButton>
      <ListItemButton
        component={NavLink}
        to="/diagnostics"
        selected={location.pathname === '/diagnostics'}
        sx={{ borderRadius: 1.5 }}
      >
        <ListItemText
          primary="Diagnostics"
          sx={{ '& .MuiListItemText-primary': { fontSize: 14 } }}
        />
      </ListItemButton>
    </List>
  );
}

export function ShellLayout({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.2 }}>
            Inai Inventory
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            <TenantBadge />
          </Box>
          {/* Cross-app switcher: the platform's app registry, filtered to this user. */}
          <PlatformAppSwitcher current="inventory" issuer={SHELL_CONFIG.authUrl} accessToken={token} />
          <AccountMenu />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Toolbar />
        <Navigation />
      </Drawer>

      <Box component="main" sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, pb: 8 }}>
        <Toolbar />
        <Box sx={{ maxWidth: 1280, mx: 'auto', pt: 3 }}>{children}</Box>
      </Box>
    </Box>
  );
}
